import { Body2, Heading2, Heading4, Image, useDeviceType } from '@cian/ui-kit';
import { IconActionChevronRight16 } from '@cian/ui-kit-design-tokens/icons';
import cn from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';
import { ShadowWrapper } from 'shared/components/ShadowWrapper';
import { NBSP, PERCENT, RUB } from 'shared/constants/common';
import { ETestIds } from 'shared/constants/testIds';
import { useShortOfferTermsModal } from 'shared/hooks/useShortOfferTermsModal';
import { ICalculatorOfferModal } from 'shared/repositories/mortgage-broker-calculator/entities/modal/CalculatorOfferModal';
import { IOfferRate } from 'shared/repositories/mortgage-broker-calculator/entities/mortgage_listing/OfferRate';
import { selectBankTitlesToIdsMap } from 'shared/selectors/dictionaries';
import { selectActionUrl } from 'shared/selectors/landing';
import { formatNumberWithSpaces, transformNumToString } from 'shared/utils/common';
import { getBankLogoUrl } from 'shared/utils/offers/mapCalculatorOfferModalToTermsModal';
import { ETargetElementForAnalytics } from 'shared/utils/tracking/types';
import { useTracking } from 'shared/utils/tracking/useTracking';
import { IOfferBtnProps } from '../OfferCard/components';

import styles from './styles.css';

export interface IShortOfferCardProps {
  bankName: string;
  iconPath: string;
  monthlyPayment?: number | null;
  income?: number | null;
  rate?: IOfferRate | null;
  modal?: ICalculatorOfferModal | null;
  button?: IOfferBtnProps;
  placeOnList: number;
}

const getDisplayRate = (rate?: IOfferRate | null): string | null => {
  if (!rate) {
    return null;
  }

  const value = rate.discount && rate.rateWithDiscount ? rate.rateWithDiscount : rate.rate;

  return `${transformNumToString(value)}${PERCENT}`;
};

export const ShortOfferCard: React.FC<IShortOfferCardProps> = ({
  bankName,
  iconPath,
  monthlyPayment,
  income,
  rate,
  modal,
  button,
  placeOnList,
}) => {
  const deviceType = useDeviceType();
  const bankTitlesToIdsMap = useSelector(selectBankTitlesToIdsMap);
  const actionUrl = useSelector(selectActionUrl) as string;
  const { handleModalOpen } = useShortOfferTermsModal();
  const { trackClickOnCard } = useTracking();
  const displayRate = getDisplayRate(rate);

  const paymentText = monthlyPayment ? `От${NBSP}${formatNumberWithSpaces(monthlyPayment)}${NBSP}${RUB}/мес` : null;

  const handleClickCard = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    trackClickOnCard({
      place: placeOnList,
      bank: bankName,
      isMultiOffer: false,
      type: button ? button.analyticEventKey : 'oshk',
    });

    if (modal?.rows?.length || modal?.terms?.length) {
      handleModalOpen(
        {
          modal,
          iconPath,
        },
        {
          authorizationRequired: true,
          bankName,
          path: button ? button.path : actionUrl,
          place: placeOnList,
          analyticEventKey: button ? button.analyticEventKey : 'oshk',
          targetElementForAnalytics: ETargetElementForAnalytics.CARD,
          isMultiOffer: false,
          text: button ? button.text : undefined,
        },
      );
    }
  };

  return (
    <div className={styles['card']}>
      <ShadowWrapper variant="shortOffer" onClick={handleClickCard}>
        <div
          className={cn(styles['root'], styles[deviceType])}
          data-testid={`${ETestIds.ShortOfferCard} bankId-${bankTitlesToIdsMap[bankName]}`}
        >
          {deviceType === 'phone' ? (
            <>
              <div className={styles['header']}>
                <div className={styles['info']}>
                  {paymentText ? <Heading2 fontWeight="700">{paymentText}</Heading2> : null}
                  <Body2 color="text-secondary-default">{bankName}</Body2>
                </div>
                <div className={styles['logo']}>
                  <Image src={getBankLogoUrl(iconPath)} title={bankName} alt={bankName} preloader />
                </div>
              </div>
              <div className={styles['stats']}>
                {displayRate ? (
                  <div className={styles['stat']}>
                    <Body2 color="text-secondary-default">Ставка</Body2>
                    <Heading4 fontWeight="700">{displayRate}</Heading4>
                  </div>
                ) : null}
                {income ? (
                  <div className={styles['stat']}>
                    <Body2 color="text-secondary-default">Доход</Body2>
                    <Heading4 fontWeight="700">{`От${NBSP}${formatNumberWithSpaces(income)}${NBSP}${RUB}`}</Heading4>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <div className={styles['main']}>
                <div className={styles['logo']}>
                  <Image src={getBankLogoUrl(iconPath)} title={bankName} alt={bankName} preloader />
                </div>
                <div className={styles['info']}>
                  <Body2 color="text-secondary-default">{bankName}</Body2>
                  {paymentText ? (
                    <div className={styles['payment']}>
                      <Heading2 fontWeight="700">{paymentText}</Heading2>
                      <span className={styles['chevron']}>
                        <IconActionChevronRight16 color="text-secondary-default" />
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles['stats']}>
                {income ? (
                  <div className={styles['stat']}>
                    <Body2 color="text-secondary-default">Доход</Body2>
                    <Heading4 fontWeight="700">{`От${NBSP}${formatNumberWithSpaces(income)}${NBSP}${RUB}`}</Heading4>
                  </div>
                ) : null}
                {displayRate ? (
                  <div className={cn(styles['stat'], styles['stat-rate'])}>
                    <Body2 color="text-secondary-default">Ставка</Body2>
                    <Heading4 fontWeight="700">{displayRate}</Heading4>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </ShadowWrapper>
    </div>
  );
};
